import { Lock } from 'lucide-react';

/**
 * The read-only half of a profile, shown rather than omitted.
 *
 * profileService.js documents the split — Azure AD owns role, reporting line,
 * department, office, email and phone; Vantagepoint owns the hire date — while
 * photos, bios, skills and interests are user-managed. That split was invisible:
 * EditProfileModal simply left every system field out, so somebody with a wrong
 * job title opened Edit Profile, could not find "title", and reasonably concluded
 * the app was broken.
 *
 * Showing the fields as locked, naming their source, and pointing at the
 * corrections route answers the question the user actually arrived with.
 */

const SOURCES = {
  AAD: 'Azure AD',
  VP: 'Vantagepoint',
};

function SystemField({ label, value, source }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-line last:border-b-0">
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-4">{label}</p>
        <p className="text-[12.5px] text-ink-2 mt-0.5 truncate">{value || '—'}</p>
      </div>
      <span className="flex-none inline-flex items-center gap-1 mt-0.5 text-[9.5px] font-semibold
                       uppercase tracking-[0.06em] text-ink-4">
        <Lock size={9} />
        {source}
      </span>
    </div>
  );
}

export function SystemFieldsPanel({ employee, manager, isAdmin = false, onRequestCorrection }) {
  if (!employee) return null;

  return (
    <div className="rounded-card border border-line bg-surface-2 overflow-hidden">
      <div className="px-3.5 py-2.5 border-b border-line">
        <p className="text-[12px] font-bold text-ink">System of record</p>
        <p className="text-[11px] text-ink-3 mt-0.5 leading-snug">
          These come from Azure AD and Vantagepoint and cannot be edited here.
          Everything on the tabs above is yours to change.
        </p>
      </div>

      <div className="px-3.5 py-1">
        <SystemField label="Job title" value={employee.jobTitle} source={SOURCES.AAD} />
        <SystemField label="Department" value={employee.department} source={SOURCES.AAD} />
        <SystemField label="Office" value={employee.officeLocation} source={SOURCES.AAD} />
        <SystemField label="Manager" value={manager?.displayName} source={SOURCES.AAD} />
        <SystemField label="Work email" value={employee.mail} source={SOURCES.AAD} />
        <SystemField label="Hire date" value={employee.hireDate} source={SOURCES.VP} />
      </div>

      <div className="px-3.5 py-2.5 border-t border-line">
        <p className="text-[11px] text-ink-3 leading-snug">
          {isAdmin
            ? 'Wrong? You can override these in Settings → User Management, which records a correction without changing the source system.'
            : 'Wrong? Ask an admin to record a correction — corrections override the source without waiting on an HR or IT change.'}
        </p>
        {onRequestCorrection && (
          <button
            onClick={onRequestCorrection}
            className="mt-2 text-[11px] font-semibold text-accent hover:underline"
          >
            {isAdmin ? 'Open User Management' : 'Request a correction'}
          </button>
        )}
      </div>
    </div>
  );
}
